/**
 * AdminAlternativeUses
 * --------------------
 * Internal admin page that lists the 10 alternative-use cards and lets Sheldon
 * log calls against each one. Not linked from any public surface, protected by
 * the standard authenticated Layout.
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, Info, HelpCircle, ChevronDown, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import CallLog from "@/components/CallLog";

const STATE_LABEL = {
  public_figure: "Sourced public figure",
  built_with_owner: "We build this with you",
  unknown: "Not shown",
};
const STATE_ICON = {
  public_figure: BookOpen,
  built_with_owner: Info,
  unknown: HelpCircle,
};

export default function AdminAlternativeUses() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState({});
  useEffect(() => {
    api.get("/public/alternative-uses").then((r) => setItems(r.data.alternatives || []));
  }, []);
  const toggle = (slug) => setOpen((o) => ({ ...o, [slug]: !o[slug] }));

  return (
    <div className="space-y-6" data-testid="admin-alt-uses-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Alternative Uses — Call Log</h1>
        <p className="text-sm text-slate-500 max-w-2xl mt-2">
          The 10 alternative uses shown on <code className="text-xs bg-slate-100 px-1 rounded">/provider</code>.
          Log calls against any of them here — insurance ALE agencies, travel-nurse housing operators, master-lease
          operators, etc. Each call is what moves a card from <em>built with owner</em> to a real, sourced figure.
        </p>
      </div>

      <div className="space-y-3">
        {items.map((c) => {
          const Icon = STATE_ICON[c.state] || Info;
          const isOpen = !!open[c.slug];
          return (
            <Card key={c.slug} className="p-4 border-slate-200" data-testid={`admin-alt-${c.slug}`}>
              <button type="button" className="w-full text-left flex items-start gap-3" onClick={() => toggle(c.slug)}>
                <div className="mt-1 text-slate-400">{isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-semibold text-slate-900 leading-tight">
                      {c.order}. {c.title_en}
                    </h3>
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700 font-semibold uppercase tracking-wider shrink-0">
                      <Icon className="w-3 h-3" /> {STATE_LABEL[c.state]}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    <code className="bg-slate-100 px-1 rounded">{c.slug}</code>
                  </div>
                </div>
              </button>
              {isOpen && (
                <div className="mt-4">
                  <CallLog
                    targetType="alternative_use"
                    targetId={c.slug}
                    targetLabel={c.title_en}
                  />
                </div>
              )}
            </Card>
          );
        })}
        {items.length === 0 && <div className="text-sm text-slate-500">Loading…</div>}
      </div>
    </div>
  );
}
